// @flow

import { mergeMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { combineEpics, ofType } from 'redux-observable';
import includes from 'lodash/includes';

import { mapAttestationUItoAPI } from '@kiosk/common/helpers/mappers';

import type { ActionsObservable, StateObservable, Middleware } from '../common/typings';
import { LOG_IN, LOG_IN_SUCCESS, LOG_IN_FAILURE, SIGN_OUT } from '../authentication/actions';
import { navigate, setAppState } from '../core/actions';
import { requestFailure } from '../utils/actions';
import {
  APP_STATE_CLOCKED_IN,
  APP_STATE_CHOOSE_SHIFT,
  APP_STATE_ON_BREAK,
  APP_STATE_END_BREAK,
  APP_STATE_CLOCKED_OUT,
  APP_STATE_NOTHING_TO_DO
} from '../constants';

import {
  GET_TIME_CLOCK_STATE,
  CLOCK_IN,
  CLOCK_IN_SUCCESS,
  START_BREAK,
  START_AUTOMATED_BREAK,
  START_BREAK_SUCCESS,
  END_BREAK,
  END_BREAK_SUCCESS,
  CLOCK_OUT,
  CLOCK_OUT_SUCCESS,
  AD_HOC_CLOCK_IN,
  AD_HOC_CLOCK_IN_SUCCESS,
  ADD_OR_EDIT_TIMECLOCK_NOTE,
  CLOCK_OUT_WITH_ATTESTATIONS,
  setTimeClockData,
  clearTimeClockData,
  getTimeClockState,
  updateShiftList,
  updateShift,
  clockInSuccess,
  startBreakSuccess,
  endBreakSuccess,
  clockOutSuccess,
  startAdHocClockIn,
  adHocClockInSuccess,
  getTimeClockStateFinished,
  addOrEditTimeclockNote,
  addOrEditTimeclockNoteSuccess,
  setHideGreeting
} from './actions';
import extendShiftModel from './utils/extendShiftModel';

export function mapLogInEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(LOG_IN, LOG_IN_FAILURE, SIGN_OUT),
    mergeMap(() => of(clearTimeClockData(), navigate(null), setAppState(APP_STATE_CHOOSE_SHIFT)))
  );
}

export function mapLogInSuccessEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(LOG_IN_SUCCESS),
    mergeMap(action => {
      const { data } = action.payload;
      const { usTimeFormat } = data.settings;
      const locationIds = data.kiosk.personas.filter(p => p.type === 'location').map(p => p.id);

      data.orgSettings.shiftBreakType = Boolean(data.orgSettings.shiftBreakType);
      data.user.personas = data.user.personas.filter(persona => {
        if (persona.type !== 'location') {
          return true;
        }
        return includes(locationIds, persona.id);
      });

      const { clockInAdHocEnabled, shiftBreakType } = data.orgSettings;
      const settings = {
        usTimeFormat,
        clockInAdHocEnabled,
        shiftBreakType,
        tags: data.tags
      };

      return [setTimeClockData(data), getTimeClockState(data.org.id, settings)];
    })
  );
}

export function getTimeClockStateEpic(
  action$: ActionsObservable,
  state$: StateObservable,
  { Api }: Middleware
) {
  return action$.pipe(
    ofType(GET_TIME_CLOCK_STATE),
    mergeMap(action => {
      const { orgId, settings } = action.payload;
      const { usTimeFormat, clockInAdHocEnabled, shiftBreakType, tags } = settings;
      const sdkContext = { shiftBreakType, usTimeFormat, tags };
      // NOTE: shifts current is also in sdk - but it sucks that whole calendarcache need to be initialized before calling that API
      return Api.apiGet(`${orgId}/shifts/current`).pipe(
        mergeMap(({ response: shiftsCurrent }) => {
          if (shiftsCurrent.length) {
            const shiftCurrent = extendShiftModel(shiftsCurrent[0], sdkContext);

            if (shiftCurrent.clockInStatus.clockedIn) {
              return Api.apiGet(`${orgId}/shifts/${shiftCurrent.id}`, {
                includeTimesheets: 'full'
              }).pipe(
                mergeMap(({ response: shiftWithTimesheetProjections }) => {
                  const shiftFinal = extendShiftModel(shiftWithTimesheetProjections, sdkContext);
                  return of(
                    setAppState(
                      shiftFinal.clockInStatus.onBreak ? APP_STATE_ON_BREAK : APP_STATE_CLOCKED_IN
                    ),
                    updateShift(shiftFinal)
                  );
                })
              );
            }

            return Api.apiGet(`${orgId}/shifts/clockin`).pipe(
              mergeMap(({ response }) => {
                if (response.length) {
                  if (response.length === 1) {
                    const shift = extendShiftModel(response[0], sdkContext);
                    return of(updateShift(shift)); // fetching timesheetprojections is not needed here as all projections are included in initial payload
                  } else {
                    const shifts = response.map(shift => extendShiftModel(shift, sdkContext));
                    return of(updateShiftList(shifts));
                  }
                }

                if (clockInAdHocEnabled) {
                  const shift = extendShiftModel(shiftsCurrent[0], sdkContext);
                  return of(startAdHocClockIn(shift));
                }

                return of(
                  getTimeClockStateFinished(),
                  navigate(APP_STATE_NOTHING_TO_DO, { shift: shiftCurrent }),
                  setAppState(APP_STATE_NOTHING_TO_DO)
                );
              }),
              catchError(error => of(requestFailure(error)))
            );
          }

          if (clockInAdHocEnabled) {
            return of(startAdHocClockIn(null));
          }

          return of(
            getTimeClockStateFinished(),
            navigate(APP_STATE_NOTHING_TO_DO),
            setAppState(APP_STATE_NOTHING_TO_DO)
          );
        }),
        catchError(error => of(requestFailure(error)))
      );
    })
  );
}

export function clockInEpic(
  action$: ActionsObservable,
  state$: StateObservable,
  { Api }: Middleware
) {
  return action$.pipe(
    ofType(CLOCK_IN),
    mergeMap(action => {
      const { shiftId } = action.payload;
      const orgId = state$.value.timeClock.data.org.id;

      return Api.apiPost(`${orgId}/timeclock/clockin`, { shiftId }).pipe(
        map(({ response }) => clockInSuccess(response)),
        catchError(error => of(requestFailure(error)))
      );
    })
  );
}

export function clockInSuccessEpic(action$: ActionsObservable, state$: StateObservable) {
  return action$.pipe(
    ofType(CLOCK_IN_SUCCESS),
    mergeMap(action => {
      const usTimeFormat = state$.value.timeClock.data.settings.usTimeFormat;
      const shiftBreakType = state$.value.timeClock.data.orgSettings.shiftBreakType;
      const tags = state$.value.timeClock.data.tags;
      const sdkContext = { usTimeFormat, shiftBreakType, tags };
      const shift = extendShiftModel(action.payload.shift, sdkContext);

      return of(
        updateShift(shift),
        navigate(APP_STATE_CLOCKED_IN, {
          clockedInLocal: shift.clockInStatus.lastProjection.lastActionLocal
        }),
        setAppState(APP_STATE_CLOCKED_IN)
      );
    })
  );
}

export function startBreakEpic(
  action$: ActionsObservable,
  state$: StateObservable,
  { Api }: Middleware
) {
  return action$.pipe(
    ofType(START_BREAK),
    mergeMap(() => {
      const orgId = state$.value.timeClock.data.org.id;

      return Api.apiPost(`${orgId}/timeclock/break`).pipe(
        map(({ response }) => startBreakSuccess(response)),
        catchError(error => of(requestFailure(error)))
      );
    })
  );
}

export function startBreakSuccessEpic(action$: ActionsObservable, state$: StateObservable) {
  return action$.pipe(
    ofType(START_BREAK_SUCCESS),
    mergeMap(action => {
      const usTimeFormat = state$.value.timeClock.data.settings.usTimeFormat;
      const shiftBreakType = state$.value.timeClock.data.orgSettings.shiftBreakType;
      const tags = state$.value.timeClock.data.tags;
      const sdkContext = { usTimeFormat, shiftBreakType, tags };
      const shift = extendShiftModel(action.payload.shift, sdkContext);

      return of(
        updateShift(shift),
        navigate(APP_STATE_ON_BREAK, {
          startBreakInLocal: shift.clockInStatus.lastProjection.lastActionLocal
        }),
        setAppState(APP_STATE_ON_BREAK)
      );
    })
  );
}

export function startAutomatedBreakEpic(
  action$: ActionsObservable,
  state$: StateObservable,
  { Api }: Middleware
) {
  return action$.pipe(
    ofType(START_AUTOMATED_BREAK),
    mergeMap(({ payload }) => {
      const orgId = state$.value.timeClock.data.org.id;

      return Api.apiPost(`${orgId}/timeclock/break`, {
        automatedType: payload.automatedBreakType
      }).pipe(
        mergeMap(({ response }) => [startBreakSuccess(response), setHideGreeting(false)]),
        catchError(error => of(requestFailure(error)))
      );
    })
  );
}

export function endBreakEpic(
  action$: ActionsObservable,
  state$: StateObservable,
  { Api }: Middleware
) {
  return action$.pipe(
    ofType(END_BREAK),
    mergeMap(() => {
      const orgId = state$.value.timeClock.data.org.id;

      return Api.apiDelete(`${orgId}/timeclock/break`).pipe(
        map(({ response }) => endBreakSuccess(response)),
        catchError(error => of(requestFailure(error)))
      );
    })
  );
}

export function endBreakSuccessEpic(action$: ActionsObservable, state$: StateObservable) {
  return action$.pipe(
    ofType(END_BREAK_SUCCESS),
    mergeMap(action => {
      const usTimeFormat = state$.value.timeClock.data.settings.usTimeFormat;
      const shiftBreakType = state$.value.timeClock.data.orgSettings.shiftBreakType;
      const tags = state$.value.timeClock.data.tags;
      const sdkContext = { usTimeFormat, shiftBreakType, tags };
      const shift = extendShiftModel(action.payload.shift, sdkContext);

      return of(
        updateShift(shift),
        navigate(APP_STATE_END_BREAK, {
          endBreakInLocal: shift.clockInStatus.lastProjection.lastActionLocal
        }), // @TODO: state break end
        setAppState(APP_STATE_END_BREAK)
      );
    })
  );
}

export function clockOutEpic(
  action$: ActionsObservable,
  state$: StateObservable,
  { Api }: Middleware
) {
  return action$.pipe(
    ofType(CLOCK_OUT),
    mergeMap(() => {
      const orgId = state$.value.timeClock.data.org.id;

      return Api.apiPost(`${orgId}/timeclock/clockout`).pipe(
        map(({ response }) => clockOutSuccess(response)),
        catchError(error => of(requestFailure(error)))
      );
    })
  );
}

export function clockOutWithAttestationsEpic(
  action$: ActionsObservable,
  state$: StateObservable,
  { Api }: Middleware
) {
  return action$.pipe(
    ofType(CLOCK_OUT_WITH_ATTESTATIONS),
    mergeMap(action => {
      const orgId = state$.value.timeClock.data.org.id;
      const { shiftId, attestations } = action.payload;
      const attestationsToApi = attestations.map(mapAttestationUItoAPI);

      return Api.apiPost(
        `${orgId}/timeclock/clockout`,
        { shiftId },
        { attestations: attestationsToApi }
      ).pipe(
        map(({ response }) => clockOutSuccess(response)),
        catchError(error => of(requestFailure(error)))
      );
    })
  );
}

export function clockOutSuccessEpic(action$: ActionsObservable, state$: StateObservable) {
  return action$.pipe(
    ofType(CLOCK_OUT_SUCCESS),
    mergeMap(action => {
      const usTimeFormat = state$.value.timeClock.data.settings.usTimeFormat;
      const shiftBreakType = state$.value.timeClock.data.orgSettings.shiftBreakType;
      const tags = state$.value.timeClock.data.tags;
      const sdkContext = { usTimeFormat, shiftBreakType, tags };
      const shift = extendShiftModel(action.payload.shift, sdkContext);

      return of(
        updateShift(shift),
        navigate(APP_STATE_CLOCKED_OUT, {
          clockOutInLocal: shift.clockInStatus.lastProjection.lastActionLocal
        }),
        setAppState(APP_STATE_CLOCKED_OUT)
      );
    })
  );
}

export function adHocClockInEpic(
  action$: ActionsObservable,
  state$: StateObservable,
  { Api }: Middleware
) {
  return action$.pipe(
    ofType(AD_HOC_CLOCK_IN),
    mergeMap(action => {
      const { locationId, positionId, timeClockNote } = action.payload;
      const orgId = state$.value.timeClock.data.org.id;
      const usTimeFormat = state$.value.timeClock.data.settings.usTimeFormat;
      const shiftBreakType = state$.value.timeClock.data.orgSettings.shiftBreakType;
      const tags = state$.value.timeClock.data.tags;
      const sdkContext = { usTimeFormat, shiftBreakType, tags };

      return Api.apiPost(`${orgId}/timeclock/clockin`, {
        locationId,
        positionId
      }).pipe(
        mergeMap(({ response }) => {
          const returnActions = [adHocClockInSuccess(extendShiftModel(response, sdkContext))];

          if (timeClockNote) {
            returnActions.push(addOrEditTimeclockNote(response.id, timeClockNote));
          }

          return returnActions;
        }),
        catchError(error => of(requestFailure(error)))
      );
    })
  );
}

export function adHocClockInSuccessEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(AD_HOC_CLOCK_IN_SUCCESS),
    mergeMap(action => {
      const { shift } = action.payload;
      return of(
        updateShift(shift),
        navigate(APP_STATE_CLOCKED_IN, {
          clockOutInLocal: shift.clockInStatus.lastProjection.lastActionLocal
        }),
        setAppState(APP_STATE_CLOCKED_IN)
      );
    })
  );
}

export function addOrEditTimeclockNoteEpic(
  action$: ActionsObservable,
  state$: StateObservable,
  { Api }: Middleware
) {
  return action$.pipe(
    ofType(ADD_OR_EDIT_TIMECLOCK_NOTE),
    mergeMap(action => {
      const { note, shiftId } = action.payload;
      const orgId = state$.value.timeClock.data.org.id;

      return Api.apiPut(`${orgId}/timeclock/${shiftId}/notes`, {}, { notes: note }).pipe(
        map(({ response }) => addOrEditTimeclockNoteSuccess(response.assigneeNotes)),
        catchError(error => {
          return of(requestFailure(error));
        })
      );
    })
  );
}

export default combineEpics(
  mapLogInEpic,
  mapLogInSuccessEpic,
  getTimeClockStateEpic,
  clockInEpic,
  clockInSuccessEpic,
  startBreakEpic,
  startBreakSuccessEpic,
  startAutomatedBreakEpic,
  endBreakEpic,
  endBreakSuccessEpic,
  clockOutEpic,
  clockOutSuccessEpic,
  adHocClockInEpic,
  adHocClockInSuccessEpic,
  addOrEditTimeclockNoteEpic,
  clockOutWithAttestationsEpic
);
